import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute, TeamProtectedRoute } from './components/ProtectedRoute'

// Authentication components
import { LoginForm } from './components/Auth/LoginForm'
import { SignupForm } from './components/Auth/SignupForm'

// Dashboard components
import { UserDashboard } from './components/Dashboard/UserDashboard'
import { PricingPage } from './components/PricingPage'
import { RootRedirect } from './components/RootRedirect'

// New UI components
import Index from './pages/Index'

// Legacy components (will be updated or removed)
import Dashboard from './pages/Dashboard'
import TeamDashboard from './pages/TeamDashboard'
import InstallSuccess from './pages/InstallSuccess'
import OAuthTest from './pages/OAuthTest'
import TeamLogin from './pages/TeamLogin'
import VoiceRecord from './pages/VoiceRecord'
import SlackOAuthCallback from './pages/SlackOAuthCallback'

const queryClient = new QueryClient()


function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router>
          <AuthProvider>
            <div className="min-h-screen bg-gray-50">
              <Toaster />
              <Sonner />
              <Routes>
                {/* Root route with authentication-based redirect */}
                <Route path="/" element={<RootRedirect />} />
                
                {/* Public routes */}
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/auth/login" element={<LoginForm />} />
                <Route path="/auth/signup" element={<SignupForm />} />
                
                {/* Slack OAuth callback route */}
                <Route path="/slack/oauth" element={<SlackOAuthCallback />} />
                
                {/* New UI Demo Route */}
                <Route path="/demo" element={<Index />} />
                
                {/* Protected routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <UserDashboard />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Team-specific protected routes */}
                <Route 
                  path="/dashboard/team/:teamId" 
                  element={
                    <ProtectedRoute>
                      <TeamDashboardWrapper />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Legacy routes - keeping for backward compatibility */}
                <Route path="/login" element={<TeamLogin />} />
                <Route path="/dashboard/:teamId" element={<Dashboard />} />
                <Route path="/install-success" element={<InstallSuccess />} />
                <Route path="/oauth-test" element={<OAuthTest />} />
                <Route path="/voice-record" element={<VoiceRecord />} />
              </Routes>
            </div>
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  )
}

// Wrapper component for team dashboard with proper protection
const TeamDashboardWrapper: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>()
  
  if (!teamId) {
    return <Navigate to="/dashboard" replace />
  }
  
  return (
    <TeamProtectedRoute teamId={teamId}>
      <TeamDashboard teamId={teamId} />
    </TeamProtectedRoute>
  )
}

export default App 